import { Box, Input, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useLocation, Link } from 'react-router-dom';
import translatePaymentType from '../../../../utils/translatePaymentType';
import { StickyScrollableTableContainer } from '../../../DataTable/StickyScrollableTableContainer';
import { useTenantTransactionList } from './useTransactionList';
import { PaymentInterface } from '../../../../shared/type/payment.type';
import { translatePaymentStatus } from './helper';
import { useMemo } from 'react';

export const TransactionList = () => {
  const location = useLocation();
  const { listOfPayments, isLoading, dateScope, handleStartDateChange, handleEndDateChange } =
    useTenantTransactionList();
  const columnHelper = createColumnHelper<PaymentInterface>();
  const rowHeight: number = 50;

  const calculateRowsNumber = (productsQuantity: number | undefined, isSummary: boolean) => {
    let rowNumber = 0;

    if (productsQuantity) {
      rowNumber += productsQuantity;
    }

    if (isSummary) {
      rowNumber++;
    }

    return rowNumber;
  };
  const borderColor = '#EAECF0';

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdDateTime', {
        header: 'Data zamówienia',
        cell: (info) => {
          const { createdDateTime, orderDto } = info.row.original;
          const date = new Date(createdDateTime);
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');

          return (
            <td rowSpan={orderDto?.length || 0}>
              <p className="text-grayLight-600 font-normal text-sm">
                {date.toLocaleDateString()}
                <br />
                {hours}:{minutes}
              </p>
            </td>
          );
        }
      }),
      columnHelper.accessor('orderDto', {
        header: 'Zamówienie',
        cell: (info) => {
          const { orderDto } = info.row.original;

          return (
            <>
              {!!orderDto &&
                orderDto.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      borderBottom:
                        index < (orderDto?.length || 0) - 1 ? `1px solid ${borderColor}` : 'none'
                    }}>
                    <div
                      className="flex flex-col justify-center items-start"
                      style={{
                        height: `${rowHeight * calculateRowsNumber(item.summaryItems?.length || 0, false)}px`
                      }}>
                      {!!item?.summaryItems &&
                        item.summaryItems.map((summaryItem, i) => (
                          <div
                            key={i}
                            className="flex items-center"
                            style={{
                              height: `calc(100% / ${calculateRowsNumber(item.summaryItems?.length || 0, false)})`
                            }}>
                            <p className="font-normal text-sm text-grayLight-600">
                              {summaryItem.amount}x {summaryItem.name}{' '}
                              {summaryItem.canceled ? '[Wycofane]' : ''}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </>
          );
        }
      }),
      columnHelper.accessor('orderDto', {
        header: 'Uczeń',
        cell: (info) => {
          const { orderDto } = info.row.original;
          const path = (id: number) =>
            location.pathname.replace(
              'finance/transaction-list',
              `users/children/single-kid/${id}/orders`
            );

          return (
            <>
              {!!orderDto &&
                orderDto.map((item, index) => (
                  <div key={index} className="flex justify-start">
                    <div
                      className="flex flex-col items-start justify-center"
                      style={{
                        height: `${rowHeight * calculateRowsNumber(item.summaryItems?.length || 0, false)}px`
                      }}>
                      <p className="text-sm font-medium text-grayLight-900 underline underline-offset-4">
                        <Link to={path(item.consumerDto?.id)}>
                          {item.consumerDto?.firstName} {item.consumerDto?.lastName}{' '}
                          {item.consumerDto?.organisationUnitName}
                        </Link>
                      </p>
                    </div>
                  </div>
                ))}
            </>
          );
        }
      }),
      columnHelper.accessor('paidDateTime', {
        header: 'Data płatności',
        cell: (info) => {
          const { paidDateTime, orderDto } = info.row.original;
          const date = paidDateTime ? new Date(paidDateTime) : null;
          const hours = date ? String(date.getHours()).padStart(2, '0') : '-';
          const minutes = date ? String(date.getMinutes()).padStart(2, '0') : '-';

          return (
            <td rowSpan={orderDto?.length || 0}>
              <p className="text-grayLight-600 font-normal text-sm">
                {date ? date.toLocaleDateString() : ''}
                {date && (
                  <>
                    <br />
                    {hours}:{minutes}
                  </>
                )}
              </p>
            </td>
          );
        }
      }),
      columnHelper.accessor('paymentType', {
        header: 'Rodzaj transakcji',
        cell: (info) => {
          const { paymentType, orderDto, tpayTrId } = info.row.original;
          return (
            <td rowSpan={orderDto?.length || 0}>
              <p className="text-grayLight-600 font-normal text-sm">
                {translatePaymentType(paymentType)} ({tpayTrId})
              </p>
            </td>
          );
        }
      }),
      columnHelper.accessor('paymentStatus', {
        header: 'Status transakcji',
        cell: (info) => {
          const { paymentStatus, orderDto } = info.row.original;
          return (
            <td rowSpan={orderDto?.length || 0}>
              <p className="text-grayLight-600 font-normal text-sm">
                {translatePaymentStatus?.(paymentStatus) ?? paymentStatus}
              </p>
            </td>
          );
        }
      }),
      columnHelper.accessor('amount', {
        header: 'Wpłata (zł)',
        size: 100,
        cell: (info) => {
          const { amount, orderDto } = info.row.original;
          return (
            <td rowSpan={orderDto?.length || 0}>
              <p className="text-grayLight-900 text-sm font-medium">{(amount / 100).toFixed(2)}</p>
            </td>
          );
        }
      }),
      columnHelper.accessor('doneBy', {
        header: 'Wykonano przez',
        cell: (info) => {
          const { doneBy, orderDto } = info.row.original;
          return (
            <td rowSpan={orderDto?.length || 0}>
              <p className="text-grayLight-600 font-normal text-sm">
                {doneBy?.firstName} {doneBy?.lastName}
              </p>
            </td>
          );
        }
      })
    ],
    [columnHelper]
  );

  return (
    <section className="ParentsTable p-spacing-xl w-full h-screen md:h-[90vh] overflow-y-hidden">
      <div className="w-full mb-4 flex items-center justify-between">
        <div className="flex flex-col basis-4/5">
          <Box>
            <Text
              whiteSpace="normal"
              overflow="hidden"
              textOverflow="ellipsis"
              className="font-semibold text-base text-grayLight-900">
              Lista wpłat
            </Text>
          </Box>
        </div>
      </div>
      {listOfPayments && (
        <StickyScrollableTableContainer
          columns={columns}
          data={listOfPayments}
          isLoading={isLoading}
          filtersRightContent={
            <div className="flex items-center justify-between">
              <span className="mr-2 font-semibold">Wybierz okres dla wpłat:</span>
              <div className="w-36">
                <Input
                  required
                  color="gray"
                  type="date"
                  value={dateScope.start}
                  onChange={handleStartDateChange}
                />
              </div>
              <p className="text-grayLight-700">-</p>
              <div className="w-36">
                <Input
                  required
                  color="gray"
                  type="date"
                  value={dateScope.end}
                  onChange={handleEndDateChange}
                />
              </div>
            </div>
          }
        />
      )}
    </section>
  );
};
